import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Camera, Plus, Trash, X, CaretLeft, CaretRight } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { errText } from '../utils/errors';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_DIM = 2048;
const JPEG_Q = 0.85;

/**
 * Client-side image compression: resize so longest side <= MAX_DIM and re-encode JPEG 85%.
 * Returns a Blob (or original file if compression fails / already small + non-jpeg-friendly).
 */
async function compressImage(file) {
  if (file.size < 500 * 1024) return file;  // <500KB: don't bother
  try {
    const img = await new Promise((res, rej) => {
      const i = new Image();
      i.onload = () => res(i);
      i.onerror = rej;
      i.src = URL.createObjectURL(file);
    });
    const longest = Math.max(img.width, img.height);
    const scale = longest > MAX_DIM ? MAX_DIM / longest : 1;
    const w = Math.round(img.width * scale);
    const h = Math.round(img.height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, w, h);
    const blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', JPEG_Q));
    URL.revokeObjectURL(img.src);
    // Wrap as File so backend gets a filename
    return new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' });
  } catch {
    return file;
  }
}

export async function uploadPhoto({ studyId, volunteerCode, visitCode, device, file, zone = '', notes = '' }) {
  const compressed = await compressImage(file);
  const fd = new FormData();
  fd.append('file', compressed);
  fd.append('volunteer_code', volunteerCode);
  fd.append('visit_code', visitCode);
  fd.append('device', device);
  if (zone) fd.append('zone', zone);
  if (notes) fd.append('notes', notes);
  const { data } = await axios.post(`${API_URL}/api/studies/${studyId}/volunteer-photos`, fd, { withCredentials: true });
  return data;
}

/**
 * Cell in the Photo table — supports N photos per (volunteer × visit).
 * URS-F217.
 */
export function PhotoCell({ studyId, volunteerCode, visitCode, device, photos: initialPhotos, onChanged, lang }) {
  const [photos, setPhotos] = useState(initialPhotos || []);
  const [uploading, setUploading] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIdx, setGalleryIdx] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => { setPhotos(initialPhotos || []); }, [initialPhotos]);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const arr = [...files];
    let ok = 0, fail = 0;
    for (const f of arr) {
      try {
        const doc = await uploadPhoto({ studyId, volunteerCode, visitCode, device, file: f });
        setPhotos(p => [...p, doc]);
        ok++;
      } catch (err) {
        fail++;
        console.error('upload failed:', err);
      }
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
    if (ok) toast.success(`${ok} foto${ok > 1 ? 's' : ''} subida${ok > 1 ? 's' : ''}`);
    if (fail) toast.error(`${fail} foto${fail > 1 ? 's' : ''} fallaron`);
    if (onChanged) onChanged();
  };

  const handleDelete = async (photoId) => {
    if (!window.confirm(lang === 'es' ? '¿Eliminar esta foto?' : 'Delete photo?')) return;
    try {
      await axios.delete(`${API_URL}/api/studies/${studyId}/volunteer-photos/${photoId}`, { withCredentials: true });
      setPhotos(p => p.filter(x => x.photo_id !== photoId));
      setGalleryOpen(false);
      toast.success(lang === 'es' ? 'Foto eliminada' : 'Photo deleted');
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(errText(e, 'Error'));
    }
  };

  const handleDeleteAll = async () => {
    if (photos.length === 0) return;
    if (!window.confirm(lang === 'es' ? `¿Eliminar las ${photos.length} fotos de esta celda?` : `Delete all ${photos.length} photos?`)) return;
    try {
      await Promise.all(photos.map(p =>
        axios.delete(`${API_URL}/api/studies/${studyId}/volunteer-photos/${p.photo_id}`, { withCredentials: true })
      ));
      setPhotos([]);
      toast.success(lang === 'es' ? 'Fotos eliminadas' : 'Photos deleted');
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(errText(e, 'Error'));
    }
  };

  const openGallery = (idx) => {
    setGalleryIdx(idx);
    setGalleryOpen(true);
  };

  return (
    <>
      <div className="min-h-[48px] flex items-center gap-1 flex-wrap" data-testid={`photo-cell-${volunteerCode}-${visitCode}-${device}`}>
        {photos.map((p, idx) => (
          <div key={p.photo_id} className="relative group">
            <button
              type="button"
              onClick={() => openGallery(idx)}
              title={`${p.original_filename || ''} · ${p.taken_by || ''}`}
              className="w-10 h-10 rounded-sm border border-stone-200 overflow-hidden bg-stone-50 hover:border-fuchsia-400 transition block"
            >
              <img
                src={`${API_URL}/api/studies/${studyId}/volunteer-photos/${p.photo_id}/image`}
                alt=""
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); handleDelete(p.photo_id); }}
              title={lang === 'es' ? 'Eliminar foto' : 'Delete photo'}
              data-testid={`photo-delete-${p.photo_id}`}
              className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-600 hover:bg-red-700 text-white opacity-0 group-hover:opacity-100 transition flex items-center justify-center shadow-sm"
            >
              <X size={9} weight="bold" />
            </button>
          </div>
        ))}
        <label
          className={`w-10 h-10 rounded-sm border-2 border-dashed flex items-center justify-center cursor-pointer transition ${uploading ? 'border-fuchsia-300 bg-fuchsia-50 animate-pulse' : 'border-stone-300 hover:border-fuchsia-400 hover:bg-fuchsia-50'}`}
          title={lang === 'es' ? 'Subir foto(s)' : 'Upload photo(s)'}
        >
          {uploading ? <span className="text-[9px] text-fuchsia-700">...</span> : (photos.length === 0 ? <Camera size={14} className="text-stone-400" /> : <Plus size={14} className="text-stone-500" />)}
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            disabled={uploading}
            onChange={(e) => handleFiles(e.target.files)}
            data-testid={`photo-upload-${volunteerCode}-${visitCode}`}
          />
        </label>
        {photos.length >= 2 && (
          <button
            type="button"
            onClick={handleDeleteAll}
            title={lang === 'es' ? `Borrar las ${photos.length} fotos` : `Delete all ${photos.length} photos`}
            data-testid={`photo-delete-all-${volunteerCode}-${visitCode}-${device}`}
            className="w-10 h-10 rounded-sm border-2 border-dashed border-red-200 hover:border-red-400 hover:bg-red-50 flex items-center justify-center text-red-500 transition"
          >
            <Trash size={13} />
          </button>
        )}
      </div>

      <Dialog open={galleryOpen} onOpenChange={setGalleryOpen}>
        <DialogContent className="max-w-4xl max-h-[92vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-sm">
              {device} · {volunteerCode} · {visitCode}
              <span className="text-stone-400 ml-2 text-xs font-normal">({galleryIdx + 1} / {photos.length})</span>
            </DialogTitle>
          </DialogHeader>
          {photos[galleryIdx] && (
            <div className="flex-1 flex flex-col items-center min-h-0">
              <div className="flex-1 min-h-0 relative w-full flex items-center justify-center bg-stone-50 rounded-sm overflow-hidden">
                <img
                  src={`${API_URL}/api/studies/${studyId}/volunteer-photos/${photos[galleryIdx].photo_id}/image`}
                  alt=""
                  className="max-w-full max-h-[60vh] object-contain"
                />
                {photos.length > 1 && (
                  <>
                    <Button variant="outline" size="icon" className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full" onClick={() => setGalleryIdx((galleryIdx - 1 + photos.length) % photos.length)}>
                      <CaretLeft size={16} />
                    </Button>
                    <Button variant="outline" size="icon" className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full" onClick={() => setGalleryIdx((galleryIdx + 1) % photos.length)}>
                      <CaretRight size={16} />
                    </Button>
                  </>
                )}
              </div>
              <div className="w-full mt-3 flex items-center justify-between gap-2 text-xs text-stone-500">
                <div className="space-y-0.5">
                  <div><b>Archivo:</b> {photos[galleryIdx].original_filename}</div>
                  <div><b>Subido por:</b> {photos[galleryIdx].taken_by} · {(photos[galleryIdx].taken_at || '').slice(0, 16).replace('T', ' ')}</div>
                  <div><b>Tamaño:</b> {Math.round((photos[galleryIdx].size || 0) / 1024)} KB</div>
                </div>
                <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(photos[galleryIdx].photo_id)}>
                  <Trash size={13} className="mr-1" />{lang === 'es' ? 'Eliminar' : 'Delete'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
